import {
  capitalizeFirst,
  formatText,
  formatWeightLb,
  getBookedQuoteFromQuotes,
  getCommdityNameForCondensedShipmentShipment,
  getEquipmentTypesDisplay as getEquipmentTypesAsSingleString,
  getServiceTypeDisplayLabel,
  ISOStringToShorthandDate,
  toFullReadableAddress,
} from "utils";
import { formatPrice } from "utils/quotes";

function getShipmentDetailsSection(
  shipment: CondensedShipment,
  acceptedQuote: CondensedQuote | undefined,
  includePrice: boolean,
  includeCarrierInfo: boolean,
) {
  const shipmentInfo = shipment?.info;
  const items: { label: string; value: string }[] = [];

  // added as long as the shipment has an accepted booking
  if (acceptedQuote && includeCarrierInfo) {
    items.push(
      {
        label: "Carrier Company",
        value: acceptedQuote.created_by_company?.name || "",
      },
      {
        label: "Carrier Contact",
        value:
          acceptedQuote.created_by?.first_name &&
          acceptedQuote.created_by?.last_name
            ? `${acceptedQuote.created_by.first_name} ${acceptedQuote.created_by.last_name}`
            : "",
      },
    );
  }

  // always added
  items.push(
    {
      label: "Commodity",
      value: shipment?.info
        ? capitalizeFirst(
            formatText(
              getCommdityNameForCondensedShipmentShipment(shipment.info),
            ),
          )
        : "",
    },
    {
      label: "Weight",
      value:
        shipmentInfo?.weight_lb != null
          ? formatWeightLb(shipmentInfo.weight_lb, { includeKg: false })
          : "",
    },
    {
      label: "Service",
      value: getServiceTypeDisplayLabel(shipmentInfo?.service ?? ""),
    },
    {
      label: "Special Requirements",
      value: shipment?.info?.special_requirements || "",
    },
    {
      label: "Additional Details",
      value: shipment?.info?.additional_details ?? "",
    },
  );

  // only added if user chooses to include price and shipment has an accepted quote
  if (includePrice && acceptedQuote?.currency && acceptedQuote.price) {
    items.push({
      label: "Booked Price",
      value: `$${formatPrice(acceptedQuote.price.toString(), acceptedQuote.currency, acceptedQuote.quoting_unit_of_measure)}`,
    });
  }

  // added as long as the shipment has an accepted booking
  if (acceptedQuote) {
    items.push({
      label: "Booked Equipment Type",
      value: getEquipmentTypesAsSingleString([acceptedQuote.equipment_type]),
    });
  }

  return {
    id: "shipment-details",
    title: "Shipment Details",
    items,
  };
}

function getRouteSection(
  acceptedQuote: CondensedQuote | undefined,
  route: Route | null,
) {
  // nothing to add if route is not present
  if (route === null || route.route_points.length === 0) {
    return {
      id: "route-details",
      title: "Shipment Route",
      items: [],
    };
  }
  const items: { label: string; value: string }[] = [];
  const sortedPoints = route.route_points.sort(
    (a: RoutePoint, b: RoutePoint) => a.point_number - b.point_number,
  );
  const originPoint = sortedPoints.find(
    (p: RoutePoint) => p.stop_type === "ORIGIN",
  );
  const destinationPoint = sortedPoints.find(
    (p: RoutePoint) => p.stop_type === "DESTINATION",
  );
  const middlePoints = sortedPoints.filter(
    (p: RoutePoint) => p.stop_type === "MID",
  );

  if (originPoint) {
    items.push({
      label: "Origin",
      value: toFullReadableAddress(originPoint?.point_location) || "",
    });
  }
  // only include ETA on origin if there is an accepted quote
  if (originPoint && acceptedQuote) {
    items.push({
      label: "Origin Date ETA (dd/mm/yyyy)",
      value: acceptedQuote?.pickup_start
        ? ISOStringToShorthandDate(acceptedQuote.pickup_start)
        : "",
    });
  }

  // include all middle points if present
  if (middlePoints && middlePoints.length > 0) {
    middlePoints.forEach((point: RoutePoint) => {
      items.push({
        label: `Stop ${point.point_number - 1} Location`,
        value: toFullReadableAddress(point.point_location) || "",
      });
    });
  }

  if (destinationPoint) {
    items.push({
      label: "Destination",
      value: toFullReadableAddress(destinationPoint?.point_location) || "",
    });
  }

  if (destinationPoint && acceptedQuote) {
    items.push({
      label: "Destination Date ETA (dd/mm/yyyy)",
      value: acceptedQuote?.delivery_eta
        ? ISOStringToShorthandDate(acceptedQuote.delivery_eta)
        : "",
    });
  }

  return {
    id: "route-details",
    title: "Shipment Route",
    items,
  };
}

export function getShipmentExportPDFRenderConfig(
  shipment: CondensedShipment,
  includePrice: boolean,
  route: Route | null,
  quotes: CondensedQuote[],
  includeCarrierInfo: boolean,
) {
  const bookedQuote = getBookedQuoteFromQuotes(quotes);
  const shipmentDetails = getShipmentDetailsSection(
    shipment,
    bookedQuote,
    includePrice,
    includeCarrierInfo,
  );
  const routeDetails = getRouteSection(bookedQuote, route);
  return [shipmentDetails, routeDetails];
}
